import {
  AGENT_LABEL,
  accessOf,
  BRAND,
  isSiteRole,
  PLANS,
  READONLY_MESSAGE,
  roleLabel,
  type Account,
  type AuthUser,
  t,
} from '@procumi/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { Avatar } from './Avatar';
import { RailArt } from './RailArt';
import { Bell } from './Bell';
import { ProfileDialog } from './ProfileDialog';
import type { Toast } from '../hooks/useToast';
import type { Route } from '../routes';
import { Icon } from './Icon';
import { openSearch } from './CommandSearch';
import { isBuild } from '../lib/terms';

/* Iconițele barei laterale: caroiaj de 16, contur, culoarea butonului (vezi Icon). */

/** Patru casete: panoul de control. */
const iconPanou = (
  <Icon>
    <rect x="2" y="2.2" width="5" height="5" rx="1.2" />
    <rect x="9" y="2.2" width="5" height="3" rx="1.2" />
    <rect x="2" y="9.2" width="5" height="4.6" rx="1.2" />
    <rect x="9" y="7.2" width="5" height="6.6" rx="1.2" />
  </Icon>
);

/** Două clădiri: una înaltă, una în construcție lângă ea. */
const iconSantiere = (
  <Icon>
    <path d="M2.6 14.2V2.6h6.2v11.6" />
    <path d="M8.8 7.4h4.6v6.8" />
    <path d="M1.2 14.2h13.6" />
    <path d="M4.8 5.2h2M4.8 8h2M4.8 10.8h2" />
  </Icon>
);

/** Foaie cu colț îndoit și rânduri: cererea trimisă. */
const iconCereri = (
  <Icon>
    <path d="M9.2 1.8H4.8A1.6 1.6 0 0 0 3.2 3.4v9.2a1.6 1.6 0 0 0 1.6 1.6h6.4a1.6 1.6 0 0 0 1.6-1.6V5.4L9.2 1.8Z" />
    <path d="M9 2v3.2h3.6" />
    <path d="M5.8 8.6h4.4M5.8 11h2.8" />
  </Icon>
);

/** Clipboard cu listă: necesarul trimis de pe șantier. */
const iconNecesar = (
  <Icon>
    <path d="M5.4 2.6H4a1.2 1.2 0 0 0-1.2 1.2v9.6A1.2 1.2 0 0 0 4 14.6h8a1.2 1.2 0 0 0 1.2-1.2V3.8A1.2 1.2 0 0 0 12 2.6h-1.4" />
    <rect x="5.4" y="1.6" width="5.2" height="2.4" rx=".8" />
    <path d="M5.6 7.2h.01M5.6 10h.01M7.6 7.2h3M7.6 10h3" />
  </Icon>
);

/** Camionul: livrările de primit pe șantier. */
const iconLivrari = (
  <Icon>
    <path d="M1.5 4h8v7h-8zM9.5 6.5h3l2 2.5v2h-5" />
    <circle cx="4.5" cy="11.7" r="1.3" />
    <circle cx="11.5" cy="11.7" r="1.3" />
  </Icon>
);

/** Un document cu bifă: comenzile de achiziție. */
const iconComenzi = (
  <Icon>
    <path d="M3.2 2.6h9.6v10.8H3.2z" />
    <path d="M5.6 5.4h4.8M5.6 7.8h4.8" />
    <path d="m6 10.8 1.2 1.2 2.6-2.6" />
  </Icon>
);

/** Doi oameni: agenda de furnizori. */
const iconFurnizori = (
  <Icon>
    <circle cx="6.1" cy="5.6" r="2.4" />
    <path d="M1.9 13.6a4.2 4.2 0 0 1 8.4 0" />
    <path d="M10.6 3.5a2.4 2.4 0 0 1 0 4.2" />
    <path d="M11.6 9.8a4.2 4.2 0 0 1 2.5 3.8" />
  </Icon>
);

/** Clădire cu ușă: datele firmei. */
const iconFirma = (
  <Icon>
    <path d="M3 14.2V3.4A1.2 1.2 0 0 1 4.2 2.2h7.6A1.2 1.2 0 0 1 13 3.4v10.8" />
    <path d="M1.6 14.2h12.8" />
    <path d="M6.6 14.2v-3h2.8v3" />
    <path d="M5.6 5.2h1.2M9.2 5.2h1.2M5.6 8h1.2M9.2 8h1.2" />
  </Icon>
);

/** Bare: istoricul de prețuri. Neutre intenționat — o săgeată în sus ar sugera scumpire. */
const iconPreturi = (
  <Icon>
    <path d="M2 13.8h12" />
    <path d="M4.6 11.4V7.6M8 11.4V3.4M11.4 11.4V6.2" />
  </Icon>
);

/** Grafic cu bare și o linie: analizele (bani, luni, furnizori). */
const iconRaport = (
  <Icon>
    <path d="M4 2.2h6l2.6 2.6v9H4z" />
    <path d="M6.4 11.2V9.4M8.4 11.2V7.6M10.4 11.2V8.6" />
  </Icon>
);

const iconAnaliza = (
  <Icon>
    <path d="M2 13.8h12" />
    <path d="M3.4 10.6 6.6 7l2.6 2.2 4-4.6" />
  </Icon>
);

/** Scânteie: Atlas, agentul de achiziții. */
const iconAgent = (
  <Icon>
    <path d="M8 1.8l1.5 3.9 3.9 1.5-3.9 1.5L8 12.6 6.5 8.7 2.6 7.2l3.9-1.5z" />
    <path d="M12.6 11.2l.6 1.5 1.5.6-1.5.6-.6 1.5-.6-1.5-1.5-.6 1.5-.6z" />
  </Icon>
);

/** Săgeată în cerc: comanda din nou, la prețurile convenite. */
const iconReorder = (
  <Icon>
    <path d="M13.2 8a5.2 5.2 0 1 1-1.6-3.8" />
    <path d="M13.4 2.4v2.6h-2.6" />
  </Icon>
);

/** Plus: cererea nouă. */
const iconPlus = (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/** Cheie: administrarea Procumi, doar pentru administratori. */
const iconAdmin = (
  <Icon>
    <circle cx="5.2" cy="10.8" r="2.8" />
    <path d="M7.2 8.8 13.4 2.6" />
    <path d="M11.2 4.8l1.8 1.8M9.6 6.4l1.4 1.4" />
  </Icon>
);

export function Rail({
  route,
  go,
  account,
  onLoadDemo,
  onSignOut,
  toast,
  onUserChange,
}: {
  route: Route;
  go: (r: Route) => void;
  account: Account;
  onLoadDemo: () => void;
  onSignOut: () => void;
  toast: Toast;
  onUserChange: (patch: Partial<AuthUser>) => void;
}) {
  const [profile, setProfile] = useState(false);
  /** omul de pe șantier: în meniu are necesarul lui și livrările */
  const site = isSiteRole(account.user.role);
  /** Meniul de pe telefon: închis până îl deschizi; orice alegere îl închide. */
  const [open, setOpen] = useState(false);
  const goTo = (r: Route) => {
    setOpen(false);
    go(r);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const current = (view: Route['view']) => {
    if (view === route.view) return 'page';
    if (view === 'requisitions' && (route.view === 'requisition' || route.view === 'newRequisition'))
      return 'page';
    if (view === 'rfqs' && (route.view === 'rfq' || route.view === 'new' || route.view === 'boq'))
      return 'page';
    if (view === 'orders' && route.view === 'order') return 'page';
    if (view === 'sites' && route.view === 'site') return 'page';
    return 'false';
  };

  const plan = PLANS.find((p) => p.id === account.company.plan)?.name;
  const access = accessOf(account.company);
  const writable = access.writable;
  const nav = (view: Route['view'], label: string, icon: ReactNode, to: Route = { view } as Route) => (
    <button aria-current={current(view)} onClick={() => goTo(to)}>
      {icon}
      {label}
    </button>
  );

  return (
    <aside className="rail">
      <div className="ws">
        <div className="ws-top">
          <img className="logo" src="/brand/procumi-lockup-dark.svg" alt={BRAND.name} />
          <Bell go={goTo} />
        </div>
        <span>
          {account.company.name} ·{' '}
          {plan
            ? t('pachet {0}', plan)
            : access.state === 'trial'
              ? t('perioadă de probă')
              : access.state === 'readonly'
                ? t('fără pachet')
                : t('fără limite')}
        </span>
      </div>
      <button
        className="new"
        disabled={!writable}
        title={writable ? undefined : t(READONLY_MESSAGE)}
        onClick={() => goTo(site ? { view: 'newRequisition' } : { view: 'new' })}
      >
        {iconPlus}
        {site ? t('Necesar nou') : t('Cerere nouă')}
      </button>
      {site ? null : (
        <button type="button" className="rail-search" onClick={openSearch}>
          <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M7.2 12.4a5.2 5.2 0 1 0 0-10.4 5.2 5.2 0 0 0 0 10.4zM14 14l-3.1-3.1"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          {t('Caută')}
          <kbd>{t('Ctrl K')}</kbd>
        </button>
      )}
      <button
        type="button"
        className="menu-toggle"
        aria-expanded={open}
        aria-controls="rail-menu"
        onClick={() => setOpen(!open)}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          {open ? (
            <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          ) : (
            <path d="M3 5h12M3 9h12M3 13h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          )}
        </svg>
        <span className="menu-label">{t('Meniu')}</span>
      </button>
      {site ? (
        <nav className="nav" id="rail-menu" data-open={open} aria-label={t('Secțiuni')}>
          {nav('requisitions', t('Necesarul meu'), iconNecesar)}
          {nav('receiving', t('Livrări'), iconLivrari)}
        </nav>
      ) : (
        <nav className="nav" id="rail-menu" data-open={open} aria-label={t('Secțiuni')}>
          {nav('dashboard', t('Panou de control'), iconPanou)}
          {nav('agent', AGENT_LABEL, iconAgent)}
          {nav('requisitions', t('Necesar'), iconNecesar)}
          {nav('rfqs', t('Cereri de ofertă'), iconCereri)}
          {nav('orders', t('Comenzi'), iconComenzi)}
          {nav('reorder', t('Comandă rapidă'), iconReorder)}
          <span className="nav-l">{t('Date')}</span>
          {nav('sites', t('Șantiere'), iconSantiere)}
          {nav('suppliers', t('Furnizori'), iconFurnizori)}
          {nav('prices', t('Prețuri'), iconPreturi)}
          {nav('analysis', t('Analiză'), iconAnaliza)}
          {nav('report', t('Raport conducere'), iconRaport)}
          <span className="nav-l">{t('Cont')}</span>
          {nav('company', t('Contul firmei'), iconFirma)}
          {/* echipa Procumi: consola e separată, cu contul ei (2026-10-29) */}
          {account.features.admin ? (
            <a className="nav-ext" href="/consola" target="_blank" rel="noopener">
              {iconAdmin}
              {t('Consola echipei')}
            </a>
          ) : null}
        </nav>
      )}
      <div className="foot" data-open={open}>
        <button className="me" onClick={() => setProfile(true)} title={t('Profilul meu')}>
          <Avatar name={account.user.name} userId={account.user.id} avatarAt={account.user.avatarAt} />
          <span className="me-t">
            <b>{account.user.name}</b>
            <span>{t(roleLabel(account.user.role))}</span>
          </span>
        </button>
        {account.company.isDemo ? <button onClick={onLoadDemo}>{t('Încarcă datele demo')}</button> : null}
        <button onClick={onSignOut}>{t('Ieși din cont')}</button>
      </div>
      {isBuild() ? <RailArt /> : null}
      {profile ? (
        <ProfileDialog
          account={account}
          toast={toast}
          onChanged={onUserChange}
          onClose={() => setProfile(false)}
        />
      ) : null}
    </aside>
  );
}
